/**
 * Filtered Order actions renderer
 */
import { Component, ViewEncapsulation } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  IOrderInventory,
  COMMON_ACTION_TYPES,
} from '@shared/types';
import { ORDER_STATUS_VALUE } from '@shared/types/enums/order-status.enum';
import { Router } from '@angular/router';
import { cloneDeep, forEach, get, includes } from 'lodash';
import { OrdersService } from '@shared/services';
import { ToolsActions } from '@store/actions';

import { ORDER_ACTION_BUTTONS } from '@shared/types/entities/order/order-action-buttons.enum';
import { PdfBaseService } from '@app/shared/common/make-pdf';
import { PdfGenerateServiceV2 } from '@app/shared/common/make-pdf/v2';
import { buildOrderPdfConfig } from '@app/shared/services/orders/order-pdf-config';
import { FilteredOrderService } from '@app/shared/services/filtered-order/filtered-order.service';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-filtered-order-actions-renderer',
  templateUrl:
    './../order-actions-renderer/order-actions-renderer.component.html',
  styleUrls: [
    './../order-actions-renderer/order-actions-renderer.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class XVIFilteredOrderRendererComponent implements AgRendererComponent {
  public data: IOrderInventory;
  public actionButtons: ActionButton[];

  // eslint-disable-next-line max-params
  public constructor(
    public toolsActions: ToolsActions,
    public filteredOrderService: FilteredOrderService,
    public ordersService: OrdersService,
    public pdfBaseService: PdfBaseService,
    public pdfGenerateService: PdfGenerateServiceV2,
    private readonly router: Router,
  ) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    return false;
  }

  public edit(): void {
    const status = this.data?.status;

    this.router.navigate(
      [this.filteredOrderService.config.uiRoute, get(this, 'data._id', '')],
      {
        queryParams: {
          get_updated_items: includes(
            this.filteredOrderService.needGetUpdateItemsStatus,
            status,
          )
            ? true
            : null,
        },
      },
    );
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Print:
        this.print();
        break;
      case COMMON_ACTION_TYPES.Export:
        this.export();
        break;
      default:
        break;
    }
  }

  public async print(): Promise<void> {
    const isOpenOrCanceled = includes(
      [ORDER_STATUS_VALUE.Canceled],
      this.data.status,
    );

    const pdfConfig = buildOrderPdfConfig(
      () => this.filteredOrderService.getPdfData(this.data),
      !isOpenOrCanceled,
      null,
      true,
    );

    await this.pdfGenerateService.printPdfAsync(pdfConfig);
  }

  public async export(): Promise<void> {
    const isOpenOrCanceled = includes(
      [ORDER_STATUS_VALUE.Canceled],
      this.data.status,
    );

    const pdfConfig = buildOrderPdfConfig(
      () => this.filteredOrderService.getPdfData(this.data),
      !isOpenOrCanceled,
      null,
      true,
    );

    await this.pdfGenerateService.downloadPdfAsync(pdfConfig);
  }

  private initActionButtons(): void {
    this.actionButtons = cloneDeep(ORDER_ACTION_BUTTONS) as ActionButton[];
    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
          btn.visible = includes(
            [
              ORDER_STATUS_VALUE.Completed,
              ORDER_STATUS_VALUE.Canceled,
              ORDER_STATUS_VALUE.Rejected,
            ],
            this.data.status,
          );
          break;
        case COMMON_ACTION_TYPES.Print:
        case COMMON_ACTION_TYPES.Export:
          btn.visible = true;
          break;
        default:
          break;
      }
    });
  }
}
